// Distance & clustering utilities for EV Route Panel
// Implements cumulative distance along route polyline projection, off‑route filtering, and spatial clustering.

// Haversine distance in KM
export const haversineKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Precompute cumulative distances for polyline points [{lat,lng},...]
export const buildPolylineCumulative = (polyline = []) => {
  const cumulative = [];
  let acc = 0;
  for (let i = 0; i < polyline.length; i++) {
    if (i > 0) {
      acc += haversineKm(polyline[i - 1].lat, polyline[i - 1].lng, polyline[i].lat, polyline[i].lng);
    }
    cumulative.push(acc);
  }
  return cumulative; // distance at each point from start
};

// Project point onto polyline segments, return closest projection with cumulative distance
export const projectPointOntoPolyline = (point, polyline, cumulative) => {
  if (!point || !polyline || polyline.length < 2) return null;
  let best = null;
  for (let i = 0; i < polyline.length - 1; i++) {
    const A = polyline[i];
    const B = polyline[i + 1];
    // Convert to cartesian approximation (small segment) using lat/lng degrees
    const Ax = A.lat; const Ay = A.lng;
    const Bx = B.lat; const By = B.lng;
    const Px = point.lat; const Py = point.lng;
    const vx = Bx - Ax; const vy = By - Ay;
    const lenSq = vx * vx + vy * vy;
    let t = 0;
    if (lenSq > 0) {
      t = ((Px - Ax) * vx + (Py - Ay) * vy) / lenSq;
    }
    if (t < 0) t = 0; else if (t > 1) t = 1;
    const projLat = Ax + vx * t;
    const projLng = Ay + vy * t;
    const offRoute = haversineKm(Px, Py, projLat, projLng);
    const segLengthKm = haversineKm(A.lat, A.lng, B.lat, B.lng);
    const distanceAlong = (cumulative[i] + segLengthKm * t);
    if (!best || offRoute < best.offRouteDistanceKm) {
      best = { offRouteDistanceKm: offRoute, distanceAlongKm: distanceAlong, projection: { lat: projLat, lng: projLng }, segmentIndex: i, t };
    }
  }
  return best;
};

// Derive cumulative distance from start for a charger using route polyline; includes fallbacks.
export const computeChargerRouteDistance = (charger, polyline, cumulative) => {
  const lat = parseFloat(charger?.locationLatitude || charger?.lat || charger?.latitude || charger?.location?.coordinates?.[1]);
  const lng = parseFloat(charger?.locationLongitude || charger?.lng || charger?.longitude || charger?.location?.coordinates?.[0]);
  if (isNaN(lat) || isNaN(lng)) return { distanceFromStartKm: null, offRouteDistanceKm: null, isValid: false };
  const projection = projectPointOntoPolyline({ lat, lng }, polyline, cumulative);
  if (projection) {
    return { distanceFromStartKm: projection.distanceAlongKm, offRouteDistanceKm: projection.offRouteDistanceKm, isValid: true };
  }
  // Fallback: straight-line distance from start
  if (polyline.length) {
    const fallback = haversineKm(polyline[0].lat, polyline[0].lng, lat, lng);
    return { distanceFromStartKm: fallback, offRouteDistanceKm: fallback, isValid: true, isFallback: true };
  }
  return { distanceFromStartKm: null, offRouteDistanceKm: null, isValid: false };
};

// Extract numeric kW from formatted display like "50kW DC"
export const parseNumericKw = (display) => {
  if (!display) return null;
  const m = String(display).match(/(\d+(?:\.\d+)?)/);
  return m ? parseFloat(m[1]) : null;
};

// Spatial clustering within thresholdMeters (default 75m). Returns array of clusters.
export const clusterChargers = (chargers, thresholdMeters = 75) => {
  const thresholdKm = thresholdMeters / 1000;
  const unassigned = [...chargers];
  const clusters = [];
  while (unassigned.length) {
    const seed = unassigned.shift();
    const seedLat = seed._lat;
    const seedLng = seed._lng;
    const members = [seed];
    for (let i = unassigned.length - 1; i >= 0; i--) {
      const c = unassigned[i];
      const d = haversineKm(seedLat, seedLng, c._lat, c._lng);
      if (d <= thresholdKm) {
        members.push(c);
        unassigned.splice(i, 1);
      }
    }
    clusters.push(members);
  }
  return clusters;
};

// Select primary charger from cluster by highest kW (fall back alphabetical name)
export const selectPrimaryCharger = (cluster) => {
  if (!cluster || !cluster.length) return null;
  return cluster.reduce((best, c) => {
    const kw = c._kwNumeric || 0;
    if (!best) return c;
    if (kw > (best._kwNumeric || 0)) return c;
    return best;
  }, null);
};
